import { create } from 'zustand';
import type { MessageBlock, ToolStepBlock } from '@netsuite-ai/types';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: MessageBlock[];
  status: 'streaming' | 'complete' | 'error' | 'disconnected' | 'cancelled';
  toolCallLabel: string | null;
}

export interface Conversation {
  id: string;
  title: string;
  updatedAt: string; // ← add this
}

interface ChatState {
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Message[];
  setConversations: (conversations: Conversation[]) => void;
  setActiveConversation: (id: string | null) => void;
  setMessages: (messages: Message[]) => void;
  startStreamingMessage: (turnId: string) => void;
  appendTextDelta: (turnId: string, delta: string) => void;
  startBlock: (turnId: string, blockId: string, block: MessageBlock) => void;
  applyBlockDelta: (
    turnId: string,
    blockId: string,
    delta: Partial<MessageBlock>,
  ) => void;
  addUserMessage: (
    content: string,
    attachments?: { fileId: string; name: string }[],
  ) => string;
  finalizeMessage: (
    turnId: string,
    status: 'complete' | 'error' | 'disconnected' | 'cancelled',
  ) => void;
  updateConversationTitle: (id: string, title: string) => void;
  resumeStreamingMessage: (turnId: string) => void;
  startToolStep: (turnId: string, toolId: string, label: string) => void;
  endToolStep: (
    turnId: string,
    toolId: string,
    outcome: Partial<
      Pick<ToolStepBlock, 'status' | 'query' | 'rows' | 'ms' | 'label'>
    >,
  ) => void;
  insertErrorMessage: (conversationId: string) => void;
  quotaExceeded: boolean;
  setQuotaExceeded: (v: boolean) => void;
  applyBlockStatus: (blockId: string, status: string, result?: unknown) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  conversations: [],
  activeConversationId: null,
  messages: [],
  setConversations: (conversations) => set({ conversations }),
  setActiveConversation: (id) => set({ activeConversationId: id }),
  setMessages: (messages) => set({ messages }),
  addUserMessage: (content, attachments = []) => {
    const id = crypto.randomUUID();
    const blocks: MessageBlock[] = [];
    if (content.trim()) blocks.push({ type: 'text', text: content });
    if (attachments.length)
      blocks.push({ type: 'attachment_list', files: attachments });
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id,
          role: 'user',
          content: blocks,
          status: 'complete',
          toolCallLabel: null,
        },
      ],
    }));
    return id;
  },
  startStreamingMessage: (turnId) =>
    set((s) => {
      // Resume: the assistant message already exists — reopen it, keep content.
      if (s.messages.some((m) => m.id === turnId)) {
        return {
          messages: s.messages.map((m) =>
            m.id === turnId
              ? { ...m, status: 'streaming' as const, toolCallLabel: null }
              : m,
          ),
        };
      }
      return {
        messages: [
          ...s.messages,
          {
            id: turnId,
            role: 'assistant' as const,
            content: [],
            status: 'streaming' as const,
            toolCallLabel: null,
          },
        ],
      };
    }),
  applyBlockStatus: (blockId, status, result) =>
    set((s) => ({
      messages: s.messages.map((m) => ({
        ...m,
        content: m.content.map((b) =>
          'id' in b && b.id === blockId
            ? ({
                ...b,
                status,
                ...(result !== undefined ? { result } : {}),
              } as MessageBlock)
            : b,
        ),
      })),
    })),
  appendTextDelta: (turnId, delta) =>
    set((s) => {
      const existing = s.messages.find((m) => m.id === turnId);
      if (existing) {
        const blocks = existing.content;
        const last = blocks[blocks.length - 1];
        const updatedBlocks =
          last?.type === 'text'
            ? [...blocks.slice(0, -1), { ...last, text: last.text + delta }]
            : [...blocks, { type: 'text' as const, text: delta }];
        return {
          messages: s.messages.map((m) =>
            m.id === turnId ? { ...m, content: updatedBlocks } : m,
          ),
        };
      }
      return {
        messages: [
          ...s.messages,
          {
            id: turnId,
            role: 'assistant' as const,
            content: [{ type: 'text' as const, text: delta }],
            status: 'streaming' as const,
            toolCallLabel: null,
          },
        ],
      };
    }),
  startBlock: (turnId, blockId, block) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === turnId
          ? {
              ...m,
              content: [
                ...m.content,
                { ...block, id: blockId } as MessageBlock,
              ],
            }
          : m,
      ),
    })),
  applyBlockDelta: (turnId, blockId, delta) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === turnId
          ? {
              ...m,
              content: m.content.map((b) =>
                'id' in b && b.id === blockId
                  ? ({ ...b, ...delta } as MessageBlock)
                  : b,
              ),
            }
          : m,
      ),
    })),
  finalizeMessage: (turnId, status) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === turnId
          ? {
              ...m,
              status,
              toolCallLabel: null,
              // Mirrors the server: a step that never ended was cut off.
              content: m.content.map((b) =>
                b.type === 'tool_step' && b.status === 'running'
                  ? {
                      ...b,
                      status:
                        status === 'complete'
                          ? ('skipped' as const)
                          : ('cancelled' as const),
                    }
                  : b,
              ),
            }
          : m,
      ),
    })),
  resumeStreamingMessage: (turnId) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === turnId
          ? { ...m, status: 'streaming' as const, toolCallLabel: null }
          : m,
      ),
    })),
  startToolStep: (turnId, toolId, label) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === turnId
          ? {
              ...m,
              toolCallLabel: label,
              content: [
                ...m.content,
                {
                  type: 'tool_step' as const,
                  id: `step-${toolId}`,
                  toolId,
                  label,
                  status: 'running' as const,
                },
              ],
            }
          : m,
      ),
    })),
  endToolStep: (turnId, toolId, outcome) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === turnId
          ? {
              ...m,
              toolCallLabel: null,
              content: m.content.map((b) =>
                b.type === 'tool_step' && b.toolId === toolId
                  ? { ...b, status: 'done' as const, ...outcome }
                  : b,
              ),
            }
          : m,
      ),
    })),
  updateConversationTitle: (id, title) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === id ? { ...c, title } : c,
      ),
    })),
  insertErrorMessage: (conversationId) =>
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id: crypto.randomUUID(),
          role: 'system' as const,
          content: [],
          status: 'error' as const,
          toolCallLabel: null,
        },
      ],
    })),
  quotaExceeded: false,
  setQuotaExceeded: (v) => set({ quotaExceeded: v }),
}));
